import * as API from '@aws-amplify/api'

// copy from internals of AWS amplify, since it doesn't expose it
type DocumentType =
    | string
    | number
    | boolean
    | DocumentType[]
    | {
          [prop: string]: DocumentType
      }
    | null

type JsonValue = boolean | string | number | null
type NotAssignableToJson = bigint | symbol | ((...args: any[]) => any)

type JsonConvertable<T> = unknown extends T
    ? never
    : {
          [P in keyof T]: T[P] extends JsonValue
              ? T[P]
              : T[P] extends NotAssignableToJson
              ? never
              : JsonConvertable<T[P]>
      }

export interface RequestOptions {
    queryParams?: Record<string, string>
    headers?: Record<string, string>
}

interface RequestOptionsWithBody extends RequestOptions {
    body?: DocumentType
}

interface RequestOptionsWithAnyBody extends RequestOptions {
    body?: any
}

interface RequestOptionsWithJsonBody<T = unknown> extends RequestOptions {
    body?: JsonConvertable<T>
}

/**
 * Wrapper around aws-amplify's `API` which works like its older version.
 * Provides methods to call endpoint and get back the response body or thrown exception. Hides new support for cancelling requests.
 */
export const SimpleApiTyped = {
    async get<TResponse>(apiName: string, path: string, options?: RequestOptions): Promise<TResponse> {
        const response = await API.get({
            apiName,
            path,
            options
        }).response
        return (await response.body.json()) as TResponse
    },

    /**
     * More simple post variant which allows sending `DocumentType` in the body.
     */
    async post<TResponse>(apiName: string, path: string, options?: RequestOptionsWithBody): Promise<TResponse> {
        const response = await API.post({
            apiName,
            path,
            options
        }).response

        if (response.statusCode === 204) {
            return undefined as TResponse
        }

        return (await response.body.json()) as TResponse
    },

    /**
     * Post variant which allows dynamic check of request body's type.
     * @returns Async function which resolves to response.
     */
    postTyped<TResponse>(apiName: string, path: string) {
        return async <TRequest>(options?: RequestOptionsWithJsonBody<TRequest>): Promise<TResponse> => {
            const response = await API.post({
                apiName,
                path,
                options
            }).response

            if (response.statusCode === 204) {
                return undefined as TResponse
            }

            return (await response.body.json()) as TResponse
        }
    },

    /**
     * More simple put variant which allows sending `DocumentType` in the body.
     */
    async put<TResponse>(apiName: string, path: string, options?: RequestOptionsWithBody): Promise<TResponse> {
        const response = await API.put({
            apiName,
            path,
            options
        }).response

        if (response.statusCode === 204) {
            return undefined as TResponse
        }

        return (await response.body.json()) as TResponse
    },

    /**
     * Put variant which allows dynamic check of request body's type.
     * @returns Async function which resolves to response.
     */
    putTyped<TResponse>(apiName: string, path: string) {
        return async <TRequest>(options?: RequestOptionsWithJsonBody<TRequest>): Promise<TResponse> => {
            const response = await API.put({
                apiName,
                path,
                options
            }).response

            if (response.statusCode === 204) {
                return undefined as TResponse
            }

            return (await response.body.json()) as TResponse
        }
    },

    /**
     * More simple patch variant which allows sending `DocumentType` in the body.
     */
    async patch<TResponse>(apiName: string, path: string, options?: RequestOptionsWithBody): Promise<TResponse> {
        const response = await API.patch({
            apiName,
            path,
            options
        }).response

        if (response.statusCode === 204) {
            return undefined as TResponse
        }

        return (await response.body.json()) as TResponse
    },

    /**
     * Patch variant which allows dynamic check of request body's type.
     * @returns Async function which resolves to response.
     */
    patchTyped<TResponse>(apiName: string, path: string) {
        return async <TRequest>(options?: RequestOptionsWithJsonBody<TRequest>): Promise<TResponse> => {
            const response = await API.patch({
                apiName,
                path,
                options
            }).response

            if (response.statusCode === 204) {
                return undefined as TResponse
            }

            return (await response.body.json()) as TResponse
        }
    },

    /**
     * More simple delete variant which allows sending `DocumentType` in the body.
     */
    async del<TResponse>(apiName: string, path: string, options?: RequestOptionsWithBody): Promise<TResponse> {
        const response = await API.del({
            apiName,
            path,
            options
        }).response

        if (response.statusCode === 204) {
            return undefined as TResponse
        }

        // HACK: aws-amplify's typehints don't allow body to be sent with a delete request: https://github.com/aws-amplify/amplify-js/issues/12844
        return (await (response as any).body.json()) as TResponse
    },

    /**
     * Delete variant which allows dynamic check of request body's type.
     * @returns Async function which resolves to response.
     */
    delTyped<TResponse>(apiName: string, path: string) {
        return async <TRequest>(options?: RequestOptionsWithJsonBody<TRequest>): Promise<TResponse> => {
            const response = await API.del({
                apiName,
                path,
                options
            }).response

            if (response.statusCode === 204) {
                return undefined as TResponse
            }

            // HACK: aws-amplify's typehints don't allow body to be sent with a delete request: https://github.com/aws-amplify/amplify-js/issues/12844
            return (await (response as any).body.json()) as TResponse
        }
    },

    async head(apiName: string, path: string, options?: RequestOptions): Promise<void> {
        await API.head({
            apiName,
            path,
            options
        }).response
    }
}

/**
 * Wrapper around aws-amplify's `API` which works like its older version.
 * Provides methods to call endpoint and get back the response body or thrown exception. Hides new support for cancelling requests.
 *
 * Accepts `any` as request body and returns `any` from response body. If possible use `SimpleApiTyped` instead
 */
export const SimpleApi = {
    get(apiName: string, path: string, options?: RequestOptions): Promise<any> {
        return SimpleApiTyped.get(apiName, path, options)
    },
    post(apiName: string, path: string, options?: RequestOptionsWithAnyBody): Promise<any> {
        return SimpleApiTyped.post(apiName, path, options)
    },
    put(apiName: string, path: string, options?: RequestOptionsWithAnyBody): Promise<any> {
        return SimpleApiTyped.put(apiName, path, options)
    },
    patch(apiName: string, path: string, options?: RequestOptionsWithAnyBody): Promise<any> {
        return SimpleApiTyped.patch(apiName, path, options)
    },
    del(apiName: string, path: string, options?: RequestOptionsWithAnyBody): Promise<any> {
        return SimpleApiTyped.del(apiName, path, options)
    },
    head(apiName: string, path: string, options?: RequestOptions): Promise<void> {
        return SimpleApiTyped.head(apiName, path, options)
    }
}

export function isApiError(error: any): error is API.ApiError {
    return error && error instanceof API.ApiError
}

/**
 * Tries to extract "errorMessage" from response body if `error` is ApiError.
 * Otherwise it returns stringified `error`.
 */
export function getApiErrorMessage(error: any): string {
    if (isApiError(error) && error.response?.body) {
        try {
            return JSON.parse(error.response.body)['errorMessage'] ?? `${error}`
        } catch {
            // nothing
        }
    }

    return `${error}`
}
